// 2026-06-25: Process Master — defines shop types (the L7 layer in the hierarchy).
// Replaces the hardcoded shop_type enum. Read by Machine Group, Operations Map,
// MES Cockpit, WO Workbench. Stored in TB customer attribute 'process_master'.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import './processmaster.css';
import ProcessMasterAdd from './processmasteradd';
import ProcessMasterEdit from './processmasteredit';
import { customerbasedshift } from '../../Services/app/operatorservice';
import { shiftadd } from '../../Services/app/masterservice';
import { getShopImage } from '../../Shared/shopImages';
import Swal from 'sweetalert2';

// Default 7 processes seeded for any customer who hasn't customised yet.
const DEFAULT_PROCESSES = [
  { code: 'MACHINE_SHOP',    name: 'Machine Shop',    color: '#2563eb', workflow_order: 1, description: 'CNC machining (milling, turning, drilling)', active: true },
  { code: 'DEBURRING',       name: 'Deburring',       color: '#f59e0b', workflow_order: 2, description: 'Edge cleanup and burr removal',           active: true },
  { code: 'SPECIAL_PROCESS', name: 'Special Process', color: '#dc2626', workflow_order: 3, description: 'Heat treatment, plating, coating',         active: true },
  { code: 'ASSEMBLY',        name: 'Assembly',        color: '#16a34a', workflow_order: 4, description: 'Component joining and sub-assembly',       active: true },
  { code: 'INSPECTION',      name: 'Inspection',      color: '#9333ea', workflow_order: 5, description: 'CMM, gauging, final QC sign-off',          active: true },
  { code: 'PACKING',         name: 'Packing',         color: '#6b7280', workflow_order: 6, description: 'Packaging and dispatch',                   active: true },
  { code: 'OTHER',           name: 'Other',           color: '#4b5563', workflow_order: 99, description: 'Catch-all for uncategorised processes',  active: true },
];

const ProcessMaster = () => {
  const [processes, setProcesses] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogOpenCount, setDialogOpenCount] = useState(0);
  const customerId = localStorage.getItem('CustomerID');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(null);
  const navigate = useNavigate();

  const handleCloseAddDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    setIsAddDialogOpen(false);
    setDialogOpenCount(prev => Math.max(0, prev - 1));
    fetchProcesses();
  };
  const handleCloseEditDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    setIsEditDialogOpen(false);
    setEditDialogData(null);
    setDialogOpenCount(prev => Math.max(0, prev - 1));
    fetchProcesses();
  };

  useEffect(() => { fetchProcesses(); }, []);

  const fetchProcesses = async () => {
    try {
      const r = await customerbasedshift(customerId, 'process_master');
      const all = r?.[0]?.value || [];
      if (Array.isArray(all) && all.length > 0) {
        setProcesses(all);
      } else {
        // First time — seed defaults
        const now = Date.now();
        const seeded = DEFAULT_PROCESSES.map((p, i) => ({
          id: `proc-${now}-${i}`,
          ...p,
          created_at: now,
          updated_at: now,
        }));
        await shiftadd({ process_master: seeded }, customerId, 'SERVER_SCOPE');
        setProcesses(seeded);
      }
    } catch (error) {
      console.error('Error fetching processes:', error);
      setProcesses([]);
    }
  };

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
    setDialogOpenCount(prev => prev + 1);
  };
  const handleOpenEditDialog = (proc) => {
    setEditDialogData(proc);
    setIsEditDialogOpen(true);
    setDialogOpenCount(prev => prev + 1);
  };

  const handleDelete = (proc) => {
    Swal.fire({
      title: `Delete process "${proc.name}"?`,
      text: 'Machine Groups using this shop_type will become "Unspecified".',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const updated = processes.filter(p => p.id !== proc.id);
          await shiftadd({ process_master: updated }, customerId, 'SERVER_SCOPE');
          Swal.fire('Deleted', `Process "${proc.name}" removed.`, 'success');
          fetchProcesses();
        } catch (e) {
          Swal.fire('Error', e?.message || 'Delete failed', 'error');
        }
      }
    });
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= processes.length) return;
    const arr = [...processes];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    // Re-stamp workflow_order
    arr.forEach((p, i) => { p.workflow_order = (i + 1); p.updated_at = Date.now(); });
    try {
      await shiftadd({ process_master: arr }, customerId, 'SERVER_SCOPE');
      setProcesses(arr);
    } catch (e) {
      Swal.fire('Error', 'Reorder failed: ' + (e?.message || ''), 'error');
    }
  };

  const sorted = [...processes].sort((a, b) => (a.workflow_order || 99) - (b.workflow_order || 99));

  return (
    <div className="pages" style={{ paddingBlockStart: '40px' }}>
      <div className="pagecontents">
        <div className="left-labels" style={{ padding: '1rem 2rem 1rem 0' }}>
          <div className="shift-content-1">
            <h5>Process Master</h5>
            <div className="add_new">
              <Tooltip title="Add Process">
                <IconButton className="circle" onClick={handleOpenAddDialog}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Define the manufacturing processes (shops) in production-flow order.
            Drag-equivalent reorder controls on each row. Used by Machine Group + Operations Map.
          </div>
        </div>

        <div className="process_list">
          {sorted.length === 0 ? (
            <div style={{ margin: '2rem', color: '#888' }}>No processes yet — defaults will seed on first save.</div>
          ) : (
            sorted.map((p, idx) => {
              const img = getShopImage(p.code);
              return (
                <div className="process_item process_item_clickable" key={p.id}
                     style={{ borderLeftColor: p.color || '#4b5563' }}
                     onClick={(e) => {
                       // Only navigate if click wasn't on an action button
                       if (e.target.closest('.process_actions')) return;
                       navigate(`/operation-master?shop=${encodeURIComponent(p.code)}`);
                     }}
                     title={`View ${p.name} operations`}>
                  <div className="process_order">
                    {p.workflow_order || idx + 1}
                  </div>
                  <img src={img} alt={p.name}
                       style={{ width: 52, height: 52, borderRadius: 6, objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                  <div className="process_info">
                    <div className="process_name">{p.name}</div>
                    <div className="process_meta">
                      <span style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '1px 6px', borderRadius: 3 }}>{p.code}</span>
                      {' · '}
                      <span style={{ color: p.color, fontWeight: 700 }}>● {p.color || '—'}</span>
                      {p.description && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{p.description}</div>}
                      <div style={{ fontSize: 11, color: '#f97316', fontWeight: 700, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        View operations <span style={{ fontSize: 14 }}>→</span>
                      </div>
                    </div>
                  </div>
                  <div className="process_actions">
                    <Tooltip title="Move up">
                      <span><IconButton size="small" disabled={idx === 0} onClick={() => move(idx, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span><IconButton size="small" disabled={idx === sorted.length - 1} onClick={() => move(idx, 1)}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(p)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ProcessMasterAdd
        open={isAddDialogOpen}
        handleClose={handleCloseAddDialog}
        handleAdd={handleCloseAddDialog}
        dialogOpenCount={dialogOpenCount}
        datasource={processes}
        customerId={customerId}
      />

      {editDialogData && (
        <ProcessMasterEdit
          open={isEditDialogOpen}
          handleClose={handleCloseEditDialog}
          handleEdit={handleCloseEditDialog}
          dialogOpenCount={dialogOpenCount}
          datasource={processes}
          customerId={customerId}
          editData={editDialogData}
        />
      )}
    </div>
  );
};

export default ProcessMaster;
